import { toNumber } from "#/lib/formatters";
import { STALE_TRADE_PRICE_AGE_MS } from "./trade-constants";

/** Minimal trade shape needed to derive a display price (newest first). */
export type DisplayPriceTrade = {
	px: string | number | null | undefined;
	/**
	 * Epoch milliseconds of the trade. Optional — when absent, age can't be
	 * computed so the trade is always treated as fresh (preserves the previous
	 * "always show the last trade" behavior for callers without timestamps).
	 */
	time?: number;
};

export type DisplayPriceSource = "trade" | "mark";

export type DisplayPriceResult = {
	/** The price to display. `0` only when neither a trade nor mark is usable. */
	price: number;
	/** True when a latest trade existed but was too old, so we used mark. */
	isStale: boolean;
	source: DisplayPriceSource;
};

type DeriveDisplayPriceParams = {
	/** Recent trades, newest first (index 0 is the latest). */
	trades: readonly DisplayPriceTrade[];
	/** Oracle-driven mark price — the fresh fallback / anchor. */
	markPrice: number;
	/** Current time in epoch ms (passed in for testability). */
	nowMs: number;
	/** Max age before the latest trade is considered stale. */
	maxAgeMs?: number;
};

/**
 * Derive the single "current price" shown both in the orderbook center value
 * and the browser-tab title.
 *
 * Why this exists: the orderbook center conventionally shows the last *traded*
 * price, but in illiquid / low-volume markets (notably HIP-3 "xyz" perps) the
 * most recent real trade can be minutes-to-hours old while the oracle-driven
 * mark price keeps moving. A stale last-trade then sits far from the live book
 * and disagrees badly with the mark price shown elsewhere (e.g. the tab title).
 * So:
 *
 *   - latest trade fresh (age ≤ maxAge) → show the last traded price
 *   - latest trade stale / missing      → fall back to mark price
 *
 * The rule is age-only — it deliberately does NOT consult the order book — so
 * that callers that have a book (orderbook) and callers that don't (tab title)
 * always resolve to the *same* value: last-trade when live, mark when stale.
 * (A book-band check would make the two diverge; if ever needed it should live
 * here and be applied by both callers, not bolted onto one.)
 */
export function deriveDisplayPrice({
	trades,
	markPrice,
	nowMs,
	maxAgeMs = STALE_TRADE_PRICE_AGE_MS,
}: DeriveDisplayPriceParams): DisplayPriceResult {
	const latest = trades[0];
	const tradePrice = latest ? toNumber(latest.px) : 0;

	const hasTrade = Number.isFinite(tradePrice) && tradePrice > 0;
	const hasMark = Number.isFinite(markPrice) && markPrice > 0;

	// No usable trade yet (initial load / empty feed) → anchor on mark.
	if (!hasTrade) {
		return { price: hasMark ? markPrice : 0, isStale: false, source: "mark" };
	}

	const age = typeof latest?.time === "number" ? nowMs - latest.time : 0;
	const isStale = age > maxAgeMs;

	// Stale last trade → prefer the fresh mark, but only if we actually have one.
	if (isStale && hasMark) {
		return { price: markPrice, isStale: true, source: "mark" };
	}

	return { price: tradePrice, isStale: false, source: "trade" };
}
